/**
 * SyncEngine + DriftTracker — a parte mais importante do projeto.
 *
 * Regra de ouro: NUNCA assumir que o player de referência e a captura de
 * microfone começaram no mesmo instante. Cada evento (play, pause, resume,
 * seek) guarda três relógios:
 *   - audioContext.currentTime  (relógio das amostras de voz)
 *   - performance.now()         (relógio de parede de alta resolução)
 *   - player.getCurrentTime()   (relógio da música)
 *
 * A partir deles calculamos:
 *   recordingOffset      = posição da voz na linha do tempo da música
 *   youtubeStartTimestamp/playerStartDelayMs = atraso real do play()
 *   latencyOffset        = compensação de latência (manual + medida)
 *   driftCorrection      = diferença de relógio acumulada
 */
import { clamp, linearSlope } from '../utils/misc.js';
import { firstTransient } from './analysis.js';
import type { SyncEventRecord } from '../types.js';

export interface SyncSample {
  ctxTime: number;
  mediaTime: number;
  perf: number;
}

export type SyncEvent = SyncEventRecord;

export interface SyncSnapshot {
  /** Atraso medido entre pedir play e o player começar a soar (ms). */
  playerStartDelayMs: number;
  /** Latência de entrada do microfone medida pelo navegador (ms). */
  inputLatencyMs: number;
  /** Latência de saída medida/estimada (ms). */
  outputLatencyMs: number;
  /** Ajuste manual do usuário (ms): + adianta a voz, − atrasa. */
  manualOffsetMs: number;
  /** Total aplicado ao posicionar a voz (ms). */
  totalOffsetMs: number;
  driftMs: number;
  driftPpm: number;
}

/**
 * Em qual ponto da faixa vocal devemos começar a tocar quando a música está em
 * `mediaTime`?
 *
 * totalOffsetMs > 0 significa que devemos ADIANTAR a voz: a fonte vocal
 * começa lendo amostras um pouco mais adiante (por exemplo, para compensar
 * latência de entrada medida). O sinal continua sempre explícito na UI.
 */
export function computeVocalPosition(
  mediaTime: number,
  opts: {
    manualOffsetMs?: number;
    inputLatencyMs?: number;
    outputLatencyMs?: number;
    playerStartDelayMs?: number;
    driftCorrectionMs?: number;
  } = {}
): number {
  const manual = opts.manualOffsetMs ?? 0;
  const inLat = opts.inputLatencyMs ?? 0;
  const outLat = opts.outputLatencyMs ?? 0;
  const startDelay = opts.playerStartDelayMs ?? 0;
  const drift = opts.driftCorrectionMs ?? 0;
  const total = manual + inLat + outLat + startDelay + drift;
  return Math.max(0, mediaTime + total / 1000);
}

/** Offset total em ms (para exibir e persistir). */
export function computeTotalOffsetMs(opts: {
  manualOffsetMs?: number;
  inputLatencyMs?: number;
  outputLatencyMs?: number;
  playerStartDelayMs?: number;
  driftCorrectionMs?: number;
}): number {
  return (
    (opts.manualOffsetMs ?? 0) +
    (opts.inputLatencyMs ?? 0) +
    (opts.outputLatencyMs ?? 0) +
    (opts.playerStartDelayMs ?? 0) +
    (opts.driftCorrectionMs ?? 0)
  );
}

/**
 * Auto-sync honesto por palma/clique.
 *
 * Como funciona: a base de referência emite um clique num instante conhecido
 * (`clickAtMediaTime`). O usuário palma junto. Detectamos o primeiro transiente
 * na voz e comparamos com o instante esperado. A diferença é o atraso real do
 * caminho completo (saída + entrada).
 *
 * Se o transiente não for confiável, devolvemos `confidence: 0` e NUNCA
 * fingimos precisão — o ajuste manual continua disponível.
 */
export function autoSyncFromClap(opts: {
  vocalData: Float32Array;
  sampleRate: number;
  vocalStartPosition: number;
  clickAtMediaTime: number;
  detectedOnsetSec?: number | null;
  thresholdDb?: number;
}): { offsetMs: number; confidence: number; detail: string; onsetSec: number | null } {
  const onset =
    opts.detectedOnsetSec !== undefined && opts.detectedOnsetSec !== null
      ? opts.detectedOnsetSec
      : firstTransient(opts.vocalData, opts.sampleRate, opts.thresholdDb ?? -38);

  if (onset == null) {
    return {
      offsetMs: 0,
      confidence: 0,
      detail: 'Não encontramos um ataque claro na gravação. Use o ajuste manual.',
      onsetSec: null,
    };
  }

  const onsetInSong = opts.vocalStartPosition + onset;
  const deltaMs = (onsetInSong - opts.clickAtMediaTime) * 1000;

  // Sanidade: diferenças acima de 2 s quase certamente não são a palma.
  if (!isFinite(deltaMs) || Math.abs(deltaMs) > 2000) {
    return {
      offsetMs: 0,
      confidence: 0.1,
      detail:
        'Detectamos um ataque, mas a diferença foi grande demais para ser confiável (>2 s). Use o ajuste manual.',
      onsetSec: onset,
    };
  }

  const confidence = clamp(1 - Math.abs(deltaMs) / 2000, 0.25, 0.98);
  return {
    offsetMs: deltaMs,
    confidence,
    detail: `Ataque detectado ${deltaMs >= 0 ? 'depois' : 'antes'} do clique de referência (${Math.abs(
      Math.round(deltaMs)
    )} ms).`,
    onsetSec: onset,
  };
}

export class DriftTracker {
  private samples: SyncSample[] = [];
  private maxSamples: number;
  private windowSec: number;
  /** (ctxTime, mediaTime) de referência para medir drift acumulado. */
  private anchor: SyncSample | null = null;

  constructor(opts: { maxSamples?: number; windowSec?: number } = {}) {
    this.maxSamples = opts.maxSamples ?? 240;
    this.windowSec = opts.windowSec ?? 30;
  }

  reset(anchor?: SyncSample) {
    this.samples = [];
    this.anchor = anchor ?? null;
  }

  add(sample: SyncSample) {
    if (!isFinite(sample.ctxTime) || !isFinite(sample.mediaTime)) return;
    if (!this.anchor) this.anchor = sample;
    this.samples.push(sample);
    const cutoff = sample.ctxTime - this.windowSec;
    while (this.samples.length > this.maxSamples || (this.samples.length > 8 && this.samples[0].ctxTime < cutoff)) {
      this.samples.shift();
    }
  }

  get count(): number {
    return this.samples.length;
  }

  /**
   * Drift em ppm entre o relógio da música e o relógio de áudio.
   * positivo = a música anda mais rápido que o áudio.
   */
  estimatePpm(): { ppm: number; r2: number; reliable: boolean } {
    if (this.samples.length < 8) return { ppm: 0, r2: 0, reliable: false };
    const xs = this.samples.map((s) => s.ctxTime - this.samples[0].ctxTime);
    const ys = this.samples.map((s) => s.mediaTime - this.samples[0].mediaTime);
    const fit = linearSlope(xs, ys);
    if (!fit) return { ppm: 0, r2: 0, reliable: false };
    const ppm = (fit.slope - 1) * 1e6;
    return { ppm, r2: fit.r2, reliable: fit.r2 > 0.98 && Math.abs(ppm) < 5000 };
  }

  /** Erro instantâneo (mídia − áudio) em ms, relativo à âncora. */
  errorMs(): number {
    if (!this.anchor || !this.samples.length) return 0;
    const last = this.samples[this.samples.length - 1];
    const dCtx = last.ctxTime - this.anchor.ctxTime;
    const dMedia = last.mediaTime - this.anchor.mediaTime;
    return (dMedia - dCtx) * 1000;
  }

  /** Série para gráfico (últimos N pontos). */
  series(limit = 60): { t: number; errMs: number }[] {
    if (!this.anchor) return [];
    return this.samples.slice(-limit).map((s) => ({
      t: s.ctxTime - this.anchor!.ctxTime,
      errMs: ((s.mediaTime - this.anchor!.mediaTime) - (s.ctxTime - this.anchor!.ctxTime)) * 1000,
    }));
  }
}

export class SyncEngine {
  readonly events: SyncEvent[] = [];
  readonly drift = new DriftTracker();
  playerStartDelayMs = 0;
  inputLatencyMs = 0;
  outputLatencyMs = 0;
  manualOffsetMs = 0;
  driftCorrectionMs = 0;
  driftThresholdMs = 45;
  autoSyncEnabled = true;
  private maxEvents = 500;

  record(event: Omit<SyncEvent, 'atPerf'> & { atPerf?: number }) {
    const e: SyncEvent = { atPerf: performance.now(), ...event };
    this.events.push(e);
    if (this.events.length > this.maxEvents) this.events.shift();
    return e;
  }

  sample(s: SyncSample) {
    this.drift.add(s);
  }

  setAnchor(s: SyncSample) {
    this.drift.reset(s);
    this.driftCorrectionMs = 0;
  }

  get totalOffsetMs(): number {
    return computeTotalOffsetMs({
      manualOffsetMs: this.manualOffsetMs,
      inputLatencyMs: this.inputLatencyMs,
      outputLatencyMs: this.outputLatencyMs,
      playerStartDelayMs: this.playerStartDelayMs,
      driftCorrectionMs: this.driftCorrectionMs,
    });
  }

  vocalPositionFor(mediaTime: number): number {
    return computeVocalPosition(mediaTime, {
      manualOffsetMs: this.manualOffsetMs,
      inputLatencyMs: this.inputLatencyMs,
      outputLatencyMs: this.outputLatencyMs,
      playerStartDelayMs: this.playerStartDelayMs,
      driftCorrectionMs: this.driftCorrectionMs,
    });
  }

  snapshot(): SyncSnapshot {
    const d = this.drift.estimatePpm();
    return {
      playerStartDelayMs: this.playerStartDelayMs,
      inputLatencyMs: this.inputLatencyMs,
      outputLatencyMs: this.outputLatencyMs,
      manualOffsetMs: this.manualOffsetMs,
      totalOffsetMs: this.totalOffsetMs,
      driftMs: this.drift.errorMs(),
      driftPpm: d.reliable ? d.ppm : 0,
    };
  }

  /**
   * Decide se precisamos corrigir drift. Devolve a correção proposta
   * (em ms) ou null. Quem aplica é o PlaybackEngine (nudge no player).
   */
  evaluateDrift(): { action: 'nudge-media' | 'none'; correctionMs: number; reason: string } {
    const err = this.drift.errorMs();
    const est = this.drift.estimatePpm();
    if (!this.autoSyncEnabled) return { action: 'none', correctionMs: 0, reason: 'auto-sync desligado' };
    if (!est.reliable) return { action: 'none', correctionMs: 0, reason: 'amostras insuficientes para medir drift' };
    if (Math.abs(err) < this.driftThresholdMs) return { action: 'none', correctionMs: 0, reason: 'dentro do limiar' };
    return {
      action: 'nudge-media',
      correctionMs: err,
      reason: `relógios divergiram ${err >= 0 ? '+' : '−'}${Math.abs(Math.round(err))} ms`,
    };
  }

  /** Ajuste manual em passos finos (1/5/10/50 ms), com limites seguros. */
  nudgeManual(deltaMs: number): number {
    this.manualOffsetMs = clamp(Math.round((this.manualOffsetMs + deltaMs) * 1000) / 1000, -2000, 2000);
    return this.manualOffsetMs;
  }

  setManual(ms: number): number {
    this.manualOffsetMs = clamp(Math.round(ms * 1000) / 1000, -2000, 2000);
    return this.manualOffsetMs;
  }

  clearManual() {
    this.manualOffsetMs = 0;
  }

  reset() {
    this.events.length = 0;
    this.drift.reset();
    this.driftCorrectionMs = 0;
    this.playerStartDelayMs = 0;
  }
}

export const syncEngine = new SyncEngine();
